import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { graphql, compose } from 'react-apollo';

import messages from '../../../locale/messages';
import tripReportQuery from './tripReportQuery.graphql';

// The damage scan report, shown on the trip to the host and the renter.
//
// Both see the same thing. Findings still waiting for a person to judge them
// are withheld from both — the review queue exists because some findings are
// not trustworthy yet, and a renter reading unconfirmed damage that is
// withdrawn an hour later is worse than waiting for it.
//
// When something is withheld the report says so rather than quietly showing a
// shorter list. A report with findings removed and no caveat reads as "we
// looked and found nothing", which is a stronger claim than the system can
// make.
//
// Styled inline: package.json pins bootstrap 3.4.1 while parts of this app use
// Bootstrap 5 utility classes, so a component depending on those may render
// unstyled. Only class names meaning the same thing across 3, 4 and 5 are used.

const styles = {
  panel: { border: '1px solid #e0e0e0', borderRadius: '6px', padding: '20px', marginTop: '20px', backgroundColor: '#fbfbfb' },
  heading: { fontWeight: 'bold', fontSize: '17px', marginBottom: '4px' },
  headline: { fontSize: '15px', marginBottom: '16px' },
  provisional: { backgroundColor: '#fff8e5', border: '1px solid #f0d9a0', borderRadius: '4px', padding: '12px', marginBottom: '16px', fontSize: '14px' },
  finding: { borderTop: '1px solid #eee', padding: '12px 0' },
  part: { fontWeight: 'bold' },
  severity: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', color: '#fff', fontSize: '12px', margin: '0 8px' },
  analysis: { color: '#444', marginTop: '6px', fontSize: '14px' },
  recommendations: { color: '#444', marginTop: '6px', fontSize: '14px', fontStyle: 'italic' },
  photos: { display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap' },
  photo: { maxWidth: '220px', width: '100%', borderRadius: '4px' },
  caption: { color: '#888', fontSize: '12px', marginTop: '4px' },
  muted: { color: '#666', fontSize: '14px' },
};

const SEVERITY_COLOUR = {
  Severe: '#b00020',
  Moderate: '#b8860b',
  Minor: '#0d6f8f',
  None: '#2d8f4b',
  Unknown: '#6c757d',
};

class TripReport extends React.Component {
  static propTypes = {
    reservationId: PropTypes.number.isRequired,
    tripReport: PropTypes.object,
    intl: intlShape.isRequired,
  };

  render() {
    const { tripReport, intl } = this.props;
    const result = tripReport && tripReport.getTripReport;

    if (!result || tripReport.loading) return null;

    // Not the caller's reservation, or not logged in. Silent rather than an
    // error: this sits on a trip page and a scan report is not the point of it.
    if (result.status !== 200) return null;

    // Nothing to show until the analysis finishes. Deliberately quiet — a
    // "pending" banner on every trip for hours would be noise.
    if (result.reportStatus !== 'completed') return null;

    const { damagedCount, inspectedCount, provisional, awaitingReview } = result;

    // Every other read here tolerates absent data; this one did not. A
    // completed report with findings null crashed the render on .length, which
    // is a worse outcome than showing no report at all.
    const findings = Array.isArray(result.findings) ? result.findings : [];

    // The answer to the only question either party has on opening this.
    //
    // Worded to avoid claiming more than is known: with findings still under
    // review, "no damage found so far" rather than "no damage".
    const headline = damagedCount > 0
      ? <FormattedMessage {...messages.tripReportDamageFound} values={{ count: damagedCount }} />
      : provisional
        ? <FormattedMessage {...messages.tripReportNoDamageYet} />
        : <FormattedMessage {...messages.tripReportNoDamage} />;

    return (
      <div style={styles.panel}>
        <div style={styles.heading}><FormattedMessage {...messages.tripReportHeading} /></div>
        <div style={styles.headline}>{headline}</div>

        {provisional && (
          <div style={styles.provisional}>
            <FormattedMessage {...messages.tripReportPending} values={{ count: awaitingReview }} />
          </div>
        )}

        {findings.length === 0 && (
          <div style={styles.muted}><FormattedMessage {...messages.tripReportNoParts} /></div>
        )}

        {findings.map((finding, index) => (
          <div key={`${finding.partName}-${index}`} style={styles.finding}>
            <span style={styles.part}>{finding.partName}</span>
            <span style={{ ...styles.severity, backgroundColor: SEVERITY_COLOUR[finding.severity] || SEVERITY_COLOUR.Unknown }}>
              {finding.severity}
            </span>
            {finding.analysis && <div style={styles.analysis}>{finding.analysis}</div>}

            {finding.recommendations && (
              <div style={styles.recommendations}>
                <FormattedMessage {...messages.tripReportRecommended} />: {finding.recommendations}
              </div>
            )}

            {/* How sure the model was. Shown because a host deciding whether to
                claim, and a renter deciding whether to dispute, are both making
                a decision on this finding and deserve to know. Absent on every
                report generated before the service stopped discarding it, so a
                missing value means unknown and shows nothing. */}
            {typeof finding.confidence === 'number' && (
              <div style={styles.caption}>
                <FormattedMessage
                  {...messages.tripReportConfidence}
                  values={{ percent: Math.round(finding.confidence * 100) }}
                />
              </div>
            )}

            {(finding.beforePhoto || finding.afterPhoto) && (
              <div style={styles.photos}>
                {finding.beforePhoto && (
                  <div>
                    <img src={finding.beforePhoto} alt={`${finding.partName} — ${intl.formatMessage(messages.tripReportBefore)}`} style={styles.photo} />
                    <div style={styles.caption}><FormattedMessage {...messages.tripReportBefore} /></div>
                  </div>
                )}
                {finding.afterPhoto && (
                  <div>
                    <img src={finding.afterPhoto} alt={`${finding.partName} — ${intl.formatMessage(messages.tripReportAfter)}`} style={styles.photo} />
                    <div style={styles.caption}><FormattedMessage {...messages.tripReportAfter} /></div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {inspectedCount > 0 && (
          <div style={{ ...styles.caption, marginTop: '12px' }}>
            <FormattedMessage {...messages.tripReportPartsShown} values={{ count: inspectedCount }} />
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  injectIntl,
  graphql(tripReportQuery, {
    name: 'tripReport',
    options: (props) => ({
      variables: { reservationId: props.reservationId },
      // A finding can be confirmed or rejected at any moment, so a cached report
      // may show damage that has since been ruled out.
      fetchPolicy: 'network-only',
    }),
  }),
)(TripReport);
